import React, { useState } from 'react';
import { UserPlus, Mail, Pencil, Trash2, Send, ShieldCheck, User, Loader2, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useFetch, Spinner } from '../lib/useFetch';
import { usersApi, companiesApi } from '../lib/api';

const ROLE_LABELS = { consultant: 'Consulente', client: 'Cliente' };
const ROLE_META = {
  consultant: { icon: ShieldCheck, color: '#023F80', bg: '#e0e7ff' },
  client: { icon: User, color: '#15803d', bg: '#dcfce7' },
};

const empty = { username: '', password: '', name: '', email: '', role: 'client', company: '', companies: [], menuAccess: [], readOnly: false, sendWelcome: true };

// Menu sections available for fine-grained access control.
// Keys MUST match the first path segment of the route (see Sidebar.jsx).
const MENU_SECTIONS = [
  { key: 'panoramica', label: 'Panoramica cliente' },
  { key: 'aziende', label: 'Elenco aziende' },
  { key: 'lavoratori', label: 'Lavoratori' },
  { key: 'sicurezza', label: 'Sicurezza sul lavoro' },
  { key: 'sorveglianza', label: 'Sorveglianza sanitaria' },
  { key: 'formazione', label: 'Formazione' },
  { key: 'haccp', label: 'HACCP' },
  { key: 'registri-haccp', label: 'Registri HACCP' },
  { key: 'piscina', label: 'Piscina' },
  { key: 'sopralluoghi', label: 'Sopralluoghi' },
  { key: 'registri', label: 'Registri' },
  { key: 'dpi', label: 'DPI' },
  { key: 'calendario', label: 'Calendario' },
  { key: 'comunicazioni', label: 'Comunicazioni' },
  { key: 'report', label: 'Report' },
  { key: 'operazioni-massive', label: 'Operazioni massive' },
  { key: 'mailing', label: 'Mailing' },
  { key: 'utenti', label: 'Gestione utenti' },
  { key: 'audit', label: 'Audit log' },
  { key: 'migrazione', label: 'Migrazione dati' },
  { key: 'riconciliazione', label: 'Riconciliazione' },
];

const UsersPage = () => {
  const { data: users, loading, reload } = useFetch(() => usersApi.list(), []);
  const { data: companies } = useFetch(() => companiesApi.list(), []);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setEditing(null); setForm(empty); setModalOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    const cs = Array.isArray(u.companies) && u.companies.length ? u.companies : (u.company ? [u.company] : []);
    setForm({ ...empty, ...u, companies: cs, menuAccess: u.menuAccess || [], readOnly: !!u.readOnly, password: '', sendWelcome: false });
    setModalOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, val) => setForm((f) => {
    const arr = Array.isArray(f[k]) ? f[k] : [];
    return { ...f, [k]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
  });

  const companyName = (id) => (companies || []).find((c) => c.id === id)?.name || '';

  const submit = async () => {
    if (!editing) {
      if (!form.username.trim()) return toast.error('Username obbligatorio');
      if ((form.password || '').length < 4) return toast.error('Password min 4 caratteri');
      if (form.role === 'client' && (!form.companies || form.companies.length === 0)) return toast.error("Seleziona almeno un'azienda per il cliente");
    } else {
      if (form.role === 'client' && (!form.companies || form.companies.length === 0)) return toast.error("Seleziona almeno un'azienda per il cliente");
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        role: form.role,
        companies: form.companies || [],
        company: (form.companies || [])[0] || null,
        menuAccess: form.menuAccess || [],
        readOnly: !!form.readOnly,
        email: form.email,
      };
      if (form.password) payload.password = form.password;
      if (editing) {
        await usersApi.update(editing.id, payload);
        toast.success('Utente aggiornato');
      } else {
        const res = await usersApi.create({ ...payload, username: form.username, password: form.password, sendWelcome: form.sendWelcome });
        const w = res.data.welcome;
        if (w?.status === 'sent') toast.success(`Utente creato — email di benvenuto inviata a ${form.email}`);
        else if (w?.status === 'error') toast.warning(`Utente creato, ma email NON inviata: ${w.error}`);
        else if (form.sendWelcome && !form.email) toast.info('Utente creato — nessuna email di benvenuto (manca indirizzo email)');
        else toast.success('Utente creato');
      }
      setModalOpen(false);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Eliminare l'utente "${u.username}"?`)) return;
    try { await usersApi.remove(u.id); toast.success('Utente eliminato'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const onResendWelcome = async (u) => {
    if (!u.email) return toast.error("L'utente non ha un'email impostata");
    const t = toast.loading(`Invio email a ${u.email}...`);
    try {
      const res = await usersApi.sendWelcome(u.id);
      toast.dismiss(t);
      if (res.data.status === 'sent') toast.success('Email inviata');
      else if (res.data.status === 'mocked') toast.warning('SMTP non configurato — invio simulato');
      else toast.error(`Errore: ${res.data.error || 'sconosciuto'}`);
    } catch (e) { toast.dismiss(t); toast.error(e.response?.data?.detail || 'Errore'); }
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#fef7d4] text-[#8a6608] flex items-center justify-center">
              <UserPlus size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800 leading-tight">Gestione utenti</h2>
              <p className="text-xs text-slate-500 mt-0.5">Consulenti e clienti con accesso a SafetyDesk81. Gli utenti cliente vedono solo la loro azienda.</p>
            </div>
          </div>
          <button onClick={openCreate} data-testid="new-user-btn" className="ro-hide px-4 py-2 rounded-full bg-[#fef7d4] text-[#8a6608] text-xs font-bold tracking-wider hover:bg-[#E6B800] flex items-center gap-2">
            <UserPlus size={14} /> NUOVO UTENTE
          </button>
        </div>

        <div className="space-y-2" data-testid="users-list">
          {(users || []).map((u) => {
            const meta = ROLE_META[u.role] || ROLE_META.client;
            const Icon = meta.icon;
            return (
              <div key={u.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl hover:bg-slate-50/60 flex-wrap" data-testid={`user-${u.id}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.bg, color: meta.color }}>
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-gray-800">{u.name}</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style={{ background: meta.bg, color: meta.color }}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                    <span>@<code>{u.username}</code></span>
                    {u.email && <span><Mail size={11} className="inline mr-1" />{u.email}</span>}
                    {(Array.isArray(u.companies) && u.companies.length > 0)
                      ? <span>{u.companies.length} aziend{u.companies.length === 1 ? 'a' : 'e'}</span>
                      : (u.company && <span>Azienda: {companyName(u.company) || u.company}</span>)}
                    {u.readOnly && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-slate-200 text-slate-700 uppercase">Sola lettura</span>}
                    {Array.isArray(u.menuAccess) && u.menuAccess.length > 0 && <span>{u.menuAccess.length} voci menu</span>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {u.email && (
                    <button onClick={() => onResendWelcome(u)} title="Invia email di accesso" className="ro-hide p-2 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700">
                      <Send size={14} />
                    </button>
                  )}
                  <button onClick={() => openEdit(u)} title="Modifica" className="ro-hide p-2 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => onDelete(u)} title="Elimina" className="ro-hide p-2 rounded-md bg-red-50 hover:bg-red-100 text-red-700">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={modalOpen} onOpenChange={(v) => !v && setModalOpen(false)}>
        <DialogContent className="max-w-xl" data-testid="user-modal">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica utente' : 'Nuovo utente'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Aggiorna nome, email, ruolo, azienda o reset password.'
                : "Crea un nuovo accesso a SafetyDesk81. Se inserisci un'email, verrà inviata automaticamente una email di benvenuto con le credenziali."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nome completo *</label>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="user-name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Username {editing && <span className="text-slate-400 font-normal">(non modificabile)</span>}</label>
              <Input value={form.username} onChange={(e) => set('username', e.target.value)} disabled={!!editing} data-testid="user-username" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">{editing ? 'Nuova password (opzionale)' : 'Password *'}</label>
              <Input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} placeholder={editing ? 'lascia vuoto per non modificare' : 'min 4 caratteri'} data-testid="user-password" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Email</label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="opzionale, per email di benvenuto" data-testid="user-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Ruolo</label>
              <Select value={form.role} onValueChange={(v) => set('role', v)}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Cliente</SelectItem>
                  <SelectItem value="consultant">Consulente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === 'client' && (
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Aziende accessibili * <span className="text-slate-400 font-normal">(seleziona una o più)</span></label>
                <div className="max-h-48 overflow-auto border border-slate-200 rounded-lg p-2 space-y-1" data-testid="user-companies">
                  {(companies || []).filter((c) => c.id).map((c) => (
                    <label key={c.id} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer">
                      <Checkbox checked={(form.companies || []).includes(c.id)} onCheckedChange={() => toggleArr('companies', c.id)} />
                      <span className="text-sm">{c.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">{(form.companies || []).length} aziende selezionate</p>
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1">Voci di menu accessibili <span className="text-slate-400 font-normal">(lascia vuoto per accesso completo)</span></label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1 border border-slate-200 rounded-lg p-2 max-h-56 overflow-auto" data-testid="user-menu-access">
                {MENU_SECTIONS.map((s) => (
                  <label key={s.key} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer">
                    <Checkbox checked={(form.menuAccess || []).includes(s.key)} onCheckedChange={() => toggleArr('menuAccess', s.key)} />
                    <span className="text-xs">{s.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-1">{(form.menuAccess || []).length === 0 ? 'Accesso a tutte le voci' : `${form.menuAccess.length} voci selezionate`}</p>
            </div>
            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={!!form.readOnly} onCheckedChange={(v) => set('readOnly', !!v)} data-testid="user-readonly" />
                <span className="text-sm">Account in <strong>sola lettura</strong> (nessuna modifica/eliminazione)</span>
              </label>
            </div>
            {!editing && (
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.sendWelcome} onCheckedChange={(v) => set('sendWelcome', !!v)} data-testid="send-welcome" />
                  <span className="text-sm">Invia email di benvenuto con credenziali (richiede email)</span>
                </label>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold">Annulla</button>
            <button onClick={submit} disabled={saving} data-testid="save-user" className="ro-hide px-4 py-2 rounded-lg bg-[#023F80] text-white text-xs font-bold hover:bg-[#1e293b] flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {editing ? 'Salva modifiche' : 'Crea utente'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
